import { useEffect, useState } from 'react';

export interface OnlineStatus {
  isOnline: boolean;
}

/**
 * Lightweight reactive online-status hook.
 *
 * Wraps `navigator.onLine` and the `online` / `offline` window events. Does
 * NOT perform any network IO and does NOT persist to localStorage — those
 * concerns belong to `modules/terminal/useNetworkStatus`, which adds a 30s
 * `/health_check` ping and tracks `lastOnline`.
 *
 * Use this hook from any presentation layer that only needs to gate UI on
 * connectivity (e.g. `GoogleMapEmbed` swapping to an offline notice).
 *
 * The hook attaches `online` / `offline` event listeners on mount and removes
 * the exact same handler references on unmount. No timers, no subscriptions
 * to clean up beyond the two listeners.
 */
export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState<boolean>(() => navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline };
}
